import { Box, Button } from "@mui/material";
import { DownloadIcon } from "@phosphor-icons/react";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";

export default function ReusableTable({
    columns,
    data,
    onRowClick,
    onCreate,
    children
}) {

    const csvConfig = mkConfig({
        filename: 'Sales_Data_Export',
        fieldSeparator: ',',
        decimalSeparator: '.',
        useKeysAsHeaders: true,
    });

    const extractRenderedValues = (rows, columns) => {
        return rows.map(row => {
            const obj = {};
            columns.forEach(col => {
                const colId = col.id ?? col.accessorKey;

                if (!colId) {
                    return;
                } // skip columns without accessor

                // let value;
                // try {
                //     value = row.getValue(colId); // get rendered value

                //     if (col.id === 'depositDate' || col.id === 'date') {
                //         // Special handling for date columns to get consistent format
                //         const dateValue = row.original[col.accessorKey] || row.original['depositOn'];
                //         value = dateValue ? new Date(dateValue).toLocaleDateString() : null;
                //     }
                // } catch (e) {
                //     console.warn(`Could not get value for column ${colId}:`, e);
                //     value = null;
                // }

                let value;
                try {
                    // If column has a Cell renderer, use it to get the exact displayed value
                    if (col.Cell) {
                        const cell = row.getAllCells().find(c => c.column.id === colId);
                        if (cell) {
                            const rendered = col.Cell({
                                cell,
                                row,
                                column: col,
                                table: cell.getContext().table
                            });

                            // Extract text from React element or primitive
                            if (rendered && typeof rendered === 'object') {
                                if (rendered.props && rendered.props?.label) {
                                    if (rendered.props.label === "Unknown") {
                                        value = "null";
                                    }
                                    else {
                                        value = rendered.props.label;
                                    }
                                }
                                else if (rendered.props && rendered.props?.children !== undefined) {
                                    value = rendered.props.children;

                                    // Join array children
                                    if (Array.isArray(value)) {
                                        value = value.join('');
                                    }
                                }
                                else {
                                    value = String(rendered);
                                }
                            }
                            else {
                                value = rendered;
                            }
                        }
                        else {
                            value = row.getValue(colId);
                        }
                    }
                    else {
                        // No Cell renderer, use raw value
                        value = row.getValue(colId);
                    }
                }
                catch (e) {
                    console.warn(`Could not get value for column ${colId}:`, e);
                    value = null;
                }

                // use header as the CSV column key
                const csvKey = col.header ?? colId;

                // Handle different value types for CSV, ensuring proper serialization
                if (value == null) {
                    obj[csvKey] = null;
                }
                else if (typeof value === 'object') {
                    obj[csvKey] = JSON.stringify(value);
                }
                else {
                    obj[csvKey] = value;
                }
            });
            return obj;
        });
    };

    const handleExportData = (table) => {
        // const visibleRows = table.getRowModel().rows; // get all rows currently visible (after filtering, sorting, etc.)
        const allRows = table.getPrePaginationRowModel().rows; // get all rows (before pagination)
        const exportData = extractRenderedValues(allRows, columns);
        const csv = generateCsv(csvConfig)(exportData);

        download(csvConfig)(csv);
    };

    const handleExportRows = (rows) => {
        const selectedRows = table.getSelectedRowModel().rows;
        const exportData = extractRenderedValues(selectedRows, columns);
        const csv = generateCsv(csvConfig)(exportData);
        
        download(csvConfig)(csv);
    };

    const table = useMaterialReactTable({
        columns,
        data,
        enableRowSelection: true,
        columnFilterDisplayMode: 'popover',
        positionToolbarAlertBanner: 'bottom',
        muiTableBodyRowProps: ({ row }) => ({
            onClick: onRowClick ? (event) => {
                onRowClick(row.original)();
            } : undefined,
            sx: {
                cursor: 'pointer', //you might want to change the cursor too when adding an onClick
            },
        }),
        renderTopToolbarCustomActions: ({ table }) => {
            // console.log("Table instance:", table);
            return (
                <Box>
                    <Button
                        //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                        onClick={() => handleExportData(table)}
                        startIcon={<DownloadIcon />}
                    >
                        Export All Data
                    </Button>
                    <Button
                        disabled={
                            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                        }
                        //only export selected rows
                        onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                        startIcon={<DownloadIcon />}
                    >
                        Export Selected Rows
                    </Button>
                </Box>
            );
        },
    });

    return (
        <div className="list-container">
            <fieldset>
                <div className="list-table-container table-responsive table-responsive-md">
                    <legend>
                        {onCreate && <button className="btn btn-primary" onClick={onCreate}>
                            Create
                        </button>}
                    </legend>
                    <MaterialReactTable table={table} />
                    {children}
                </div>
            </fieldset>
        </div>
    );
}